/**
 * The real object for a Texture Studio swatch (docs/TEXTURE-STUDIO-SPEC.md):
 * one opening of a style, built by the viewer's own builder exactly as the
 * room shows it -- a door in its frame, a garage door with its windows and
 * hardware, a window with its grilles and sill -- set in a stub of wall over
 * a floor, and exported as the room is (sceneExport.ts), tags and all. The
 * back end's swatch.py renders that file with the renderer's materials, so
 * the swatch is the finished item.
 */

import * as THREE from 'three';
import { OPENING_STYLES, OpeningKindId } from './openingStyles';
import { buildWallOpeningsGroup } from './openingMeshBuilder';
import { exportRoomGlb } from './sceneExport';
import type { OpeningShape } from './types';

/** The style an item key names (an opening style id), or null. */
export function itemStyle(item: string): { kind: OpeningKindId; id: string } | null {
  for (const kind of Object.keys(OPENING_STYLES) as OpeningKindId[]) {
    if (OPENING_STYLES[kind].some((s) => s.id === item)) return { kind, id: item };
  }
  return null;
}

/** Every opening style of these kinds, as items. */
export function itemsOf(kinds: OpeningKindId[], woodOnly = false): string[] {
  return kinds.flatMap((kind) => OPENING_STYLES[kind].filter((s) => !woodOnly || s.palette.wood).map((s) => s.id));
}

const WALL_FT = 16;
const WALL_HEIGHT_FT = 9;
const WALL_THICK_FT = 0.45;

/** The opening of a kind, at its usual size, in the middle of the stub wall. */
function shapeOf(kind: OpeningKindId, id: string): OpeningShape {
  if (kind === 'window') return { kind, style: id, x: WALL_FT / 2 - 2, y: 3, width: 4, height: 3 };
  if (kind === 'door') return { kind, style: id, x: WALL_FT / 2 - 1.5, y: 0, width: 3, height: 6.67 };
  return { kind: 'garageDoor', style: id, x: WALL_FT / 2 - 4.5, y: 0, width: 9, height: 7 };
}

function drywallBox(x0: number, x1: number, y0: number, y1: number): THREE.Mesh {
  const g = new THREE.BoxGeometry(x1 - x0, y1 - y0, WALL_THICK_FT);
  const m = new THREE.MeshStandardMaterial({ color: 0xd9d4c9, roughness: 0.9 });
  m.userData.kind = 'drywall';
  const mesh = new THREE.Mesh(g, m);
  mesh.position.set((x0 + x1) / 2, (y0 + y1) / 2, -WALL_THICK_FT / 2);
  return mesh;
}

/**
 * The item as a room export (.glb bytes): the opening in a drywall stub
 * wall with a floor in front, in the wall's own frame (x along, y up, z
 * towards the room). Null for a key that is no opening style.
 */
export async function itemSwatchGlb(item: string): Promise<ArrayBuffer | null> {
  const style = itemStyle(item);
  if (!style) return null;
  const shape = shapeOf(style.kind, style.id);
  const root = new THREE.Group();
  root.name = 'swatch';
  root.add(buildWallOpeningsGroup([shape], WALL_FT, WALL_HEIGHT_FT));
  // The wall round the opening in four pieces, so glass looks out at the sky.
  const x0 = shape.x, x1 = shape.x + shape.width, y0 = shape.y, y1 = shape.y + shape.height;
  if (x0 > 0) root.add(drywallBox(0, x0, 0, WALL_HEIGHT_FT));
  if (x1 < WALL_FT) root.add(drywallBox(x1, WALL_FT, 0, WALL_HEIGHT_FT));
  if (y0 > 0) root.add(drywallBox(x0, x1, 0, y0));
  if (y1 < WALL_HEIGHT_FT) root.add(drywallBox(x0, x1, y1, WALL_HEIGHT_FT));
  // The floor: a plane lying at y = 0 (sceneExport.ts calls that a floor) out into the room.
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(WALL_FT, 12), new THREE.MeshStandardMaterial({ color: 0xd4ccc0, roughness: 0.7 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(WALL_FT / 2, 0, 6);
  root.add(floor);
  root.updateMatrixWorld(true);
  return exportRoomGlb([root]);
}

/** Bytes as base64, for the swatch request. */
export function base64Of(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
